/**
 * Flip to Win - Dark
 * ----------------------------------------------------------------
 * Behavior of the "flip-win" game in the dark glass design.
 * Config comes from data-* attributes on [data-flip-win-dark] and the
 * outcome list from <script data-fw-outcomes>. No copy is hardcoded here,
 * and every instance is scoped to its own root.
 *
 * Flow (same as the original game):
 *  - The Nth card tapped always reveals outcome N (block order).
 *  - Empty card -> shake + toast.  Prize -> confetti, chip in the vault,
 *    vault + countdown timer appear.  Gold card -> charge, slow flip,
 *    flash + mega confetti, remaining cards dim, banner, winner popup.
 *  - After the game: leave-page popup (mouse leaves top / back button),
 *    optional auto-redirect, claim -> discount/cart/redirect.
 *  - Progress is kept in sessionStorage so a reload restores the board.
 *
 * QA helpers (URL params):
 *   ?fw_flips=N           show the board with N cards already flipped
 *   ?fw_modal=winner|exit open a popup (use with pw_flips)
 *   ?stay=1               disable auto-redirect
 */
(function () {
  "use strict";

  var SELECTOR = "[data-flip-win-dark]";
  var GOLD_COLORS = ["#fdd972", "#eebc3c", "#fff3c9", "#daa100", "#ffffff"];
  var PREVIEW_ORDER = [4, 0, 8, 2, 6, 1, 3, 5, 7];
  var reducedMotion = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  /* ------------------------------------------------------------------
   * Confetti (canvas, shared by all instances)
   * ------------------------------------------------------------------ */
  var Confetti = (function () {
    var canvas = null, ctx = null, parts = [], raf = null;

    function ensure() {
      if (canvas) return;
      canvas = document.createElement("canvas");
      canvas.className = "fw-confetti";
      canvas.setAttribute("aria-hidden", "true");
      document.body.appendChild(canvas);
      ctx = canvas.getContext("2d");
      size();
      window.addEventListener("resize", size);
    }

    function size() {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    function push(x, y, vx, vy, cols) {
      parts.push({
        x: x, y: y, vx: vx, vy: vy,
        w: 5 + Math.random() * 6,
        h: 8 + Math.random() * 8,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        col: cols[Math.floor(Math.random() * cols.length)],
        life: 90 + Math.random() * 60
      });
    }

    function tick() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      parts = parts.filter(function (p) { return p.life > 0 && p.y < canvas.height + 30; });
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.vy += 0.22; p.vx *= 0.985;
        p.x += p.vx; p.y += p.vy;
        p.rot += p.vr; p.life--;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.min(1, p.life / 40);
        ctx.fillStyle = p.col;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (parts.length) {
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        raf = null;
      }
    }

    function start() { if (!raf) raf = requestAnimationFrame(tick); }

    function burst(o) {
      if (reducedMotion) return;
      o = o || {};
      ensure();
      var n = o.count || 60, cols = o.colors;
      var x = typeof o.x === "number" ? o.x : window.innerWidth / 2;
      var y = typeof o.y === "number" ? o.y : window.innerHeight / 2;
      for (var i = 0; i < n; i++) {
        var a = Math.random() * Math.PI * 2, sp = 4 + Math.random() * 9;
        push(x, y, Math.cos(a) * sp, Math.sin(a) * sp - 4, cols);
      }
      start();
    }

    function rain(o) {
      if (reducedMotion) return;
      ensure();
      for (var i = 0; i < (o.count || 80); i++) {
        push(Math.random() * window.innerWidth, -20 - Math.random() * window.innerHeight * 0.5,
          (Math.random() - 0.5) * 2, 1 + Math.random() * 3, o.colors);
      }
      start();
    }

    function mega(cols) {
      if (reducedMotion) return;
      var w = window.innerWidth, h = window.innerHeight;
      burst({ x: w / 2, y: h * 0.4, count: 120, colors: cols });
      burst({ x: w * 0.12, y: h * 0.65, count: 60, colors: cols });
      burst({ x: w * 0.88, y: h * 0.65, count: 60, colors: cols });
      rain({ count: 60, colors: cols });
    }

    return { burst: burst, mega: mega };
  })();

  /* ------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------ */
  function toArray(list) { return Array.prototype.slice.call(list || []); }

  function rootUrl() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || "/";
  }

  function postJSON(path, body) {
    return fetch(rootUrl() + path, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    });
  }

  function ssGet(key) {
    try { return JSON.parse(sessionStorage.getItem(key) || "null"); } catch (e) { return null; }
  }

  function ssSet(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  function params() {
    try { return new URLSearchParams(window.location.search); } catch (e) { return { get: function () { return null; } }; }
  }

  function vibrate(p) {
    if (navigator.vibrate) { try { navigator.vibrate(p); } catch (e) {} }
  }

  function emit(name, detail) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: name };
    if (detail) for (var k in detail) payload[k] = detail[k];
    window.dataLayer.push(payload);
    try { window.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) {}
  }

  function pad(n) { return String(n).padStart(2, "0"); }

  function readConfig(root) {
    var d = root.dataset;
    return {
      destination: d.destination || "/",
      redirectToCheckout: d.redirectToCheckout === "true",
      addToCart: d.addToCart === "true",
      discount: d.discount || "",
      minutes: parseInt(d.minutes, 10) || 15,
      redirectSeconds: parseInt(d.redirectSeconds, 10) || 0,
      exitIntent: d.exitIntent === "true",
      vaultAtEnd: d.vaultAtEnd === "true",
      winnerModal: d.winnerModal === "true",
      autoFlip: d.autoFlip === "true",
      storageKey: d.storageKey || "flip_win_dark",
      cartAttrKey: d.cartAttrKey || "",
      cartAttrValue: d.cartAttrValue || "",
      ctaInitial: d.ctaInitial || "Flip [n] cards",
      ctaFlip: d.ctaFlip || "Flip [n] more cards",
      ctaFinal: d.ctaFinal || "Claim my prizes",
      finalToast: d.finalToast || "",
      confetti: d.enableConfetti === "true",
      missBadge: d.missBadge || "",
      freeText: d.freeText || "",
      iconSuffix: d.iconSuffix || d.sectionId || ""
    };
  }

  function readOutcomes(root) {
    var s = root.querySelector("script[data-fw-outcomes]");
    if (!s) return [];
    try {
      var parsed = JSON.parse(s.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* ------------------------------------------------------------------
   * One game instance
   * ------------------------------------------------------------------ */
  function init(root) {
    if (!root || root.getAttribute("data-fw-bound") === "true") return;

    var cfg = readConfig(root);
    var outcomes = readOutcomes(root);
    var cards = toArray(root.querySelectorAll("[data-fw-card]"));
    if (!outcomes.length || !cards.length) return;
    root.setAttribute("data-fw-bound", "true");

    var maxFlips = Math.min(outcomes.length, cards.length);
    var WHITE_COLORS = ["#ffffff", "#f1f5f9", "#dce6f0", "#bed1e4"];
    var inEditor = !!(window.Shopify && window.Shopify.designMode);

    function q(sel) { return root.querySelector(sel); }
    var el = {
      top: q("[data-fw-top]"),
      toast: q("[data-fw-toast]"),
      toastText: q("[data-fw-toast-text]"),
      ctaWrap: q("[data-fw-cta-wrap]"),
      cta: q("[data-fw-cta]"),
      ctaText: q("[data-fw-cta-text]"),
      count: q("[data-fw-count]"),
      banner: q("[data-fw-banner]"),
      vault: q("[data-fw-vault]"),
      timer: q("[data-fw-timer]"),
      exitMm: q("[data-fw-exit-mm]"),
      exitSs: q("[data-fw-exit-ss]"),
      winner: q("[data-fw-winner]"),
      exit: q("[data-fw-exit]"),
      flash: q("[data-fw-flash]")
    };

    var state = { flips: [], deadline: null, modalSeen: false, exitSeen: false };
    var busy = false, done = false, exitArmed = false, autoRunning = false, claiming = false;
    var timerInt = null, redirectTimer = null, preview = false, lastFocus = null;

    /* ---- restore saved progress ---- */
    var saved = ssGet(cfg.storageKey);
    if (saved && Array.isArray(saved.flips)) {
      var seen = {};
      state.flips = saved.flips.filter(function (i) {
        if (typeof i !== "number" || i < 0 || i >= cards.length || seen[i]) return false;
        seen[i] = true;
        return true;
      }).slice(0, maxFlips);
      state.deadline = typeof saved.deadline === "number" ? saved.deadline : null;
      state.modalSeen = !!saved.modalSeen;
      state.exitSeen = !!saved.exitSeen;
    }

    /* ---- QA preview via URL ---- */
    var p = params();
    var previewFlips = parseInt(p.get("fw_flips"), 10);
    if (!isNaN(previewFlips) && previewFlips >= 0) {
      preview = true;
      var order = PREVIEW_ORDER.filter(function (i) { return i < cards.length; });
      state = {
        flips: order.slice(0, Math.min(previewFlips, maxFlips)),
        deadline: Date.now() + cfg.minutes * 60000,
        modalSeen: true,
        exitSeen: true
      };
    }
    if (p.get("stay") === "1" || inEditor || preview) cfg.redirectSeconds = 0;

    function save() {
      if (preview || inEditor) return;
      ssSet(cfg.storageKey, state);
    }

    /* ---- tag the cart once per session ---- */
    if (cfg.cartAttrKey && !inEditor) {
      var attrFlag = cfg.storageKey + "_attr";
      if (ssGet(attrFlag) !== 1) {
        var attrs = {};
        attrs[cfg.cartAttrKey] = cfg.cartAttrValue;
        postJSON("cart/update.js", { attributes: attrs })
          .then(function () { ssSet(attrFlag, 1); })
          .catch(function () {});
      }
    }

    /* ---- small state helpers ---- */
    function isPrize(o) { return o && o.kind !== "dud"; }

    function wonList() {
      return state.flips.map(function (_, i) { return outcomes[i]; }).filter(isPrize);
    }

    function hasGold() {
      return wonList().some(function (o) { return o.kind === "gold"; });
    }

    function icon(name, viewBox, fill) {
      return '<svg viewBox="' + viewBox + '"' + (fill ? ' fill="' + fill + '"' : "") + ' aria-hidden="true"><use href="#fw-' + name + "-" + cfg.iconSuffix + '"></use></svg>';
    }

    /* ---- UI updates ---- */
    function setToast(text, withIcon) {
      if (!text || !el.toast) return;
      el.toastText.textContent = text;
      el.toast.classList.toggle("no-icon", !withIcon);
      el.toast.classList.remove("is-on");
      void el.toast.offsetWidth; // restart the entry animation
      el.toast.classList.add("is-on");
      if (el.top) el.top.classList.add("has-toast");
    }

    function updateCount() {
      if (el.count) el.count.textContent = wonList().length;
    }

    function updateCta() {
      if (!el.ctaText) return;
      var n = state.flips.length, text;
      if (done || n >= maxFlips) {
        text = cfg.ctaFinal;
      } else if (n === 0) {
        text = cfg.ctaInitial.split("[n]").join(maxFlips);
      } else {
        var left = maxFlips - n;
        text = cfg.ctaFlip.split("[n]").join(left);
        if (left === 1) text = text.replace(/cards\b/i, function (m) { return m.slice(0, -1); });
      }
      el.ctaText.textContent = text;
    }

    function tickTimer() {
      if (!state.deadline) return;
      var left = Math.max(0, state.deadline - Date.now());
      var s = Math.floor(left / 1000);
      var mm = pad(Math.floor(s / 60)), ss = pad(s % 60);
      if (el.timer) {
        el.timer.textContent = mm + ":" + ss;
        el.timer.classList.toggle("is-hot", left > 0 && left <= 120000);
      }
      if (el.exitMm) el.exitMm.textContent = mm;
      if (el.exitSs) el.exitSs.textContent = ss;
      if (left === 0 && timerInt) {
        clearInterval(timerInt);
        timerInt = null;
      }
    }

    function startTimer() {
      if (!state.deadline) {
        state.deadline = Date.now() + cfg.minutes * 60000;
        save();
      }
      tickTimer();
      if (!timerInt) timerInt = setInterval(tickTimer, 1000);
    }

    function showVault(animate) {
      if (!el.vault || el.vault.classList.contains("is-on")) return;
      if (cfg.vaultAtEnd && !done) return;
      if (!animate) el.vault.style.animation = "none";
      el.vault.classList.add("is-on");
      startTimer();
      if (animate) {
        setTimeout(function () { el.vault.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, 250);
      }
    }

    function addChip(order, animate) {
      toArray(root.querySelectorAll('[data-fw-chip="' + order + '"]')).forEach(function (chip) {
        if (!animate) chip.style.animation = "none";
        chip.classList.add("is-on");
      });
    }

    function renderBack(card, o) {
      var back = card.querySelector("[data-fw-back]");
      if (!back) return;
      back.innerHTML = "";
      var kind = o.kind || "prize";
      card.classList.add("is-" + kind);
      if (o.highlight) card.classList.add("is-highlight");
      var gold = kind === "gold";

      if (gold) {
        var face = card.querySelector("[data-fw-face]");
        var slot = card.querySelector("[data-fw-slot]");
        if (face) { face.classList.remove("fw__face--dark"); face.classList.add("fw__face--gold"); }
        if (slot) { slot.classList.remove("fw__slot--dark"); slot.classList.add("fw__slot--gold"); }
      }

      function span(cls, text) {
        var s = document.createElement("span");
        s.className = cls;
        if (text != null) s.textContent = text;
        return s;
      }

      if (kind === "dud") {
        if (cfg.missBadge) back.appendChild(span("fw__miss-badge", cfg.missBadge));
        back.appendChild(span("fw__miss-circle", "\u00d7"));
        if (o.name) back.appendChild(span("fw__prize-label", o.name));
        return;
      }

      if (o.image) {
        var tile = span("fw__prize-tile" + (gold ? " fw__prize-tile--gold" : ""));
        tile.appendChild(span("fw__prize-tile-bg"));
        var img = document.createElement("img");
        img.className = "fw__prize-img";
        img.src = o.image;
        img.alt = "";
        img.loading = "lazy";
        tile.appendChild(img);
        back.appendChild(tile);
      } else {
        var ic = span("fw__prize-icon");
        ic.innerHTML = gold
          ? icon("trophy", "0 0 44 40", "#430C0C")
          : icon("star-lg", "0 0 44 44", "#bed1e4");
        back.appendChild(ic);
      }

      if (o.name) back.appendChild(span("fw__prize-label" + (gold ? " fw__prize-label--gold" : ""), o.name));

      if (o.value) {
        var val = span("fw__prize-value" + (gold ? " fw__prize-value--gold" : ""));
        if (o.hideFreeText || !cfg.freeText) {
          val.textContent = o.value;
        } else {
          val.appendChild(document.createTextNode(cfg.freeText + " "));
          var s = document.createElement("s");
          s.textContent = o.value;
          val.appendChild(s);
        }
        back.appendChild(val);
      }
    }

    function flipCard(card, animate, label) {
      if (!animate) card.classList.add("fw-no-anim");
      card.classList.add("is-flipped");
      card.disabled = true;
      card.setAttribute("aria-label", "Card revealed: " + (label || "empty"));
      if (!animate) setTimeout(function () { card.classList.remove("fw-no-anim"); }, 60);
    }

    function burstAt(card, count, gold) {
      if (!cfg.confetti) return;
      var r = card.getBoundingClientRect();
      Confetti.burst({
        x: r.left + r.width / 2,
        y: r.top + r.height / 2,
        count: count,
        colors: gold ? GOLD_COLORS : WHITE_COLORS
      });
    }

    function dimRest(stagger) {
      var d = 0;
      cards.forEach(function (card, i) {
        if (state.flips.indexOf(i) !== -1) return;
        card.disabled = true;
        if (stagger) {
          setTimeout(function () { card.classList.add("is-dim"); }, d);
          d += 120;
        } else {
          card.classList.add("is-dim");
        }
      });
    }

    function showBanner(animate) {
      if (!el.banner) return false;
      if (el.ctaWrap) el.ctaWrap.style.display = "none";
      if (el.vault && !cfg.vaultAtEnd) el.vault.classList.remove("is-on");
      if (!animate) el.banner.style.animation = "none";
      el.banner.classList.add("is-on");
      if (animate) {
        setTimeout(function () { el.banner.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, 250);
      }
      return true;
    }

    /* ---- popups ---- */
    function openOverlay(ov) {
      if (!ov || ov.classList.contains("is-on")) return;
      lastFocus = document.activeElement;
      ov.classList.add("is-on");
      var first = ov.querySelector("button:not([disabled]), a[href]");
      if (first) {
        if (typeof window.trapFocus === "function") window.trapFocus(ov, first);
        else first.focus({ preventScroll: true });
      }
    }

    function closeOverlay(ov) {
      if (!ov || !ov.classList.contains("is-on")) return;
      ov.classList.remove("is-on");
      cancelRedirect();
      var back = lastFocus && document.contains(lastFocus) ? lastFocus : null;
      if (typeof window.removeTrapFocus === "function") window.removeTrapFocus(back);
      else if (back && back.focus) back.focus({ preventScroll: true });
    }

    function openWinner() {
      if (!cfg.winnerModal || !el.winner) return;
      openOverlay(el.winner);
      armRedirect();
      state.modalSeen = true;
      save();
    }

    function showExit(force) {
      if (!el.exit || !done) return;
      if (state.exitSeen && !force) return;
      if (state.deadline && state.deadline <= Date.now()) return; // nothing left to lose
      state.exitSeen = true;
      save();
      tickTimer();
      closeOverlay(el.winner);
      openOverlay(el.exit);
      armRedirect();
    }

    function armExit() {
      if (exitArmed || !cfg.exitIntent || !el.exit || inEditor || preview) return;
      exitArmed = true;

      document.addEventListener("mouseout", function (e) {
        if (state.exitSeen) return;
        if (!e.relatedTarget && e.clientY <= 0) showExit();
      });

      if (!state.exitSeen) {
        try { history.pushState({ fw: 1 }, ""); } catch (e) {}
        window.addEventListener("popstate", function () {
          if (!state.exitSeen) {
            showExit();
            try { history.pushState({ fw: 1 }, ""); } catch (e) {}
          }
        });
      }
    }

    function armRedirect() {
      if (!cfg.redirectSeconds) return;
      clearTimeout(redirectTimer);
      redirectTimer = setTimeout(go, cfg.redirectSeconds * 1000);
    }

    function cancelRedirect() {
      clearTimeout(redirectTimer);
      redirectTimer = null;
    }

    /* ---- claim ---- */
    function go() {
      cancelRedirect();
      if (claiming || inEditor) return;
      claiming = true;

      var target = cfg.redirectToCheckout ? rootUrl() + "checkout" : cfg.destination;
      if (cfg.discount) {
        target = rootUrl() + "discount/" + encodeURIComponent(cfg.discount) + "?redirect=" + encodeURIComponent(target);
      }
      var navigate = function () { window.location.href = target; };

      var won = wonList();
      emit("flip_game_claim", { prizes: won.map(function (o) { return o.name; }).join(", ") });

      var items = cfg.addToCart
        ? won.filter(function (o) { return o.variantId; }).map(function (o) {
            return { id: Number(o.variantId), quantity: Number(o.quantity) || 1 };
          })
        : [];

      var chain = Promise.resolve();
      if (cfg.cartAttrKey && won.length) {
        var attrs = {};
        attrs[cfg.cartAttrKey + " prizes"] = won.map(function (o) { return o.name; }).join(", ");
        chain = chain.then(function () { return postJSON("cart/update.js", { attributes: attrs }); });
      }
      if (items.length) {
        chain = chain.then(function () { return postJSON("cart/add.js", { items: items }); });
      }
      chain.then(navigate, navigate);
      setTimeout(navigate, 4000); // never leave the customer stuck on a slow request
    }

    /* ---- end of game ---- */
    function complete(animate, silent) {
      done = true;
      dimRest(animate);
      if (cfg.finalToast) setToast(cfg.finalToast, false);
      if (wonList().length) startTimer();

      var gold = hasGold();
      var bannerShown = false;

      function finishUi() {
        bannerShown = gold && showBanner(animate);
        if (!bannerShown) {
          updateCta();
          if (el.cta) el.cta.classList.add("fw__btn--pulse");
        }
        if (cfg.vaultAtEnd && wonList().length) showVault(animate);
        busy = false;
      }

      if (animate) setTimeout(finishUi, 700); else finishUi();

      armExit();

      if (gold && !state.modalSeen && !preview) {
        if (animate) setTimeout(openWinner, 1500); else openWinner();
      }

      if (!silent) emit("flip_game_complete", { prizes: wonList().map(function (o) { return o.name; }).join(", ") });
    }

    /* ---- gold card animation ---- */
    function goldSequence(card, o, isLast) {
      if (reducedMotion) {
        flipCard(card, false, o.name);
        addChip(state.flips.length - 1, false);
        showVault(false);
        updateCount();
        updateCta();
        setToast(o.toast, true);
        if (isLast) complete(false); else busy = false;
        return;
      }

      card.classList.add("is-charging");
      vibrate([30, 60, 30, 60, 30]);

      setTimeout(function () {
        card.classList.remove("is-charging");
        card.classList.add("is-goldflip");
        flipCard(card, true, o.name);
      }, 950);

      setTimeout(function () {
        if (el.flash) {
          el.flash.classList.remove("go");
          void el.flash.offsetWidth;
          el.flash.classList.add("go");
        }
        if (cfg.confetti) Confetti.mega(GOLD_COLORS);
        vibrate([80, 60, 80, 60, 200]);
        addChip(state.flips.length - 1, true);
        updateCount();
        updateCta();
        if (!isLast) {
          setToast(o.toast, true);
          showVault(true);
        }
      }, 2200);

      setTimeout(function () {
        if (isLast) complete(true); else busy = false;
      }, isLast ? 3000 : 2600);
    }

    /* ---- one flip ---- */
    function pick(idx) {
      if (busy || done) return;
      var card = cards[idx];
      if (!card || card.classList.contains("is-flipped")) return;
      var order = state.flips.length;
      if (order >= maxFlips) return;

      busy = true;
      state.flips.push(idx);
      save();

      if (order === 0) emit("flip_game_start");
      var o = outcomes[order];
      var isLast = state.flips.length === maxFlips;
      emit("flip_game_flip", { flip: order + 1, outcome: o.kind, prize: o.name || "" });

      renderBack(card, o);

      if (o.kind === "gold") {
        goldSequence(card, o, isLast);
        return;
      }

      if (reducedMotion) {
        flipCard(card, false, o.name);
        if (isPrize(o)) {
          addChip(order, false);
          showVault(false);
        }
        updateCount();
        updateCta();
        setToast(o.toast, isPrize(o));
        if (isLast) complete(false); else busy = false;
        return;
      }

      if (o.kind === "dud") {
        flipCard(card, true, o.name);
        vibrate(60);
        setTimeout(function () { card.classList.add("is-shake"); }, 560);
        setTimeout(function () {
          card.classList.remove("is-shake");
          setToast(o.toast, false);
          updateCta();
          if (isLast) complete(true); else busy = false;
        }, 950);
        return;
      }

      flipCard(card, true, o.name);
      setTimeout(function () {
        burstAt(card, 60, false);
        vibrate([40, 40, 80]);
        addChip(order, true);
        showVault(true);
        updateCount();
        updateCta();
        setToast(o.toast, true);
      }, 580);
      setTimeout(function () {
        if (isLast) complete(true); else busy = false;
      }, 800);
    }

    /* ---- restore board without animation ---- */
    function applyStatic() {
      state.flips.forEach(function (idx, order) {
        var card = cards[idx];
        var o = outcomes[order];
        if (!card || !o) return;
        renderBack(card, o);
        flipCard(card, false, o.name);
        if (isPrize(o)) addChip(order, false);
      });
      updateCount();
      if (wonList().length) showVault(false);

      if (state.flips.length >= maxFlips) {
        complete(false, true);
      } else if (state.flips.length > 0) {
        var last = outcomes[state.flips.length - 1];
        setToast(last.toast, isPrize(last));
      }
      updateCta();

      if (preview) {
        var m = p.get("fw_modal");
        if (m === "winner") openOverlay(el.winner);
        if (m === "exit") { tickTimer(); openOverlay(el.exit); }
      }
    }

    /* ---- events ---- */
    cards.forEach(function (card, index) {
      card.addEventListener("click", function () { pick(index); });
      card.addEventListener("pointerenter", function (e) {
        if (e.pointerType === "mouse") card.classList.add("is-hover");
      });
      card.addEventListener("pointerleave", function () { card.classList.remove("is-hover"); });
      card.addEventListener("pointercancel", function () { card.classList.remove("is-hover"); });
    });

    if (el.cta) {
      el.cta.addEventListener("click", function () {
        if (done) { go(); return; }
        if (!cfg.autoFlip || state.flips.length >= maxFlips || autoRunning) return;
        autoRunning = true;
        (function step() {
          if (done || state.flips.length >= maxFlips) {
            autoRunning = false;
            return;
          }
          if (!busy) {
            var rest = [];
            cards.forEach(function (c, i) {
              if (state.flips.indexOf(i) === -1 && !c.classList.contains("is-flipped")) rest.push(i);
            });
            if (rest.length) pick(rest[Math.floor(Math.random() * rest.length)]);
          }
          setTimeout(step, 350);
        })();
      });
    }

    toArray(root.querySelectorAll("[data-fw-claim]")).forEach(function (btn) {
      btn.addEventListener("click", go);
    });

    var dismiss = q("[data-fw-dismiss]");
    if (dismiss) {
      dismiss.addEventListener("click", function () {
        closeOverlay(el.winner);
        if (cfg.exitIntent) showExit(true);
      });
    }

    [el.winner, el.exit].forEach(function (ov) {
      if (!ov) return;
      ov.addEventListener("click", function (e) {
        if (e.target === ov) closeOverlay(ov);
      });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        closeOverlay(el.winner);
        closeOverlay(el.exit);
      }
    });

    window.addEventListener("pagehide", cancelRedirect);
    window.addEventListener("pageshow", function (e) {
      if (e.persisted) {
        busy = false;
        autoRunning = false;
        claiming = false;
      }
    });

    applyStatic();
  }

  function initAll() {
    toArray(document.querySelectorAll(SELECTOR)).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener("shopify:section:load", function (event) {
    var root = event.target.querySelector(SELECTOR);
    if (root) init(root);
  });
})();